import { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
    UncontrolledTooltip
} from 'reactstrap';
import { FaSearch, FaEdit, FaTrash, FaUser, FaPlus, FaRegBuilding, FaUsers, FaEye, FaBuilding, FaSync } from 'react-icons/fa';
import { CLIENT_FORM_PAGE } from '../../routes/routeConstants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clientActions } from '../../sagas/clientSlice';
import { formatPrice } from '../../helpers/utility';
import Swal from 'sweetalert2';

const EtatClient = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const getClientFormPath = () => {
        if (location.pathname.startsWith('/sales-module')) {
            return '/sales-module/clients/new';
        }
        return CLIENT_FORM_PAGE;
    };

    const getClientEditPath = (client) => {
        if (location.pathname.startsWith('/sales-module')) {
            return `/sales-module/clients/${client.id}/edit`;
        }
        return CLIENT_FORM_PAGE;
    };

    // États
    const { clients, loading } = useSelector(state => state.clients);
    const [filteredClients, setClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [balanceFilter, setBalanceFilter] = useState('ALL'); // 'ALL' | 'DEBITEUR' | 'CREDITEUR'


    useEffect(() => {
        dispatch(clientActions.search({
            query: ""
        }));// Appel de l'action pour récupérer les clients    
    }, []);

    // Filtrage et recherche
    useEffect(() => {
        if (loading) return;

        let result = clients;

        // Recherche
        if (searchTerm) {
            result = result.filter(client => {
                const clientName = client.typeClient === 'PARTICULIER'
                    ? `${client.nom} ${client.prenom}`
                    : client.entreprise;
                return clientName.toLowerCase().includes(searchTerm.toLowerCase())
                    || client.code.toString().includes(searchTerm);
            });
        }

        // 🔎 Filtre Débiteur / Créditeur / Tous
        if (balanceFilter === 'DEBITEUR') {
            // Débiteur = solde > 0 (client doit payer)
            result = result.filter(c => Number(c.solde) > 0);
        } else if (balanceFilter === 'CREDITEUR') {
            // Créditeur = solde < 0 (vous devez au client)
            result = result.filter(c => Number(c.solde) < 0);
        }
        // 'ALL' => aucun filtre supplémentaire

        setClients(result);
        setCurrentPage(1);
    }, [searchTerm, clients, balanceFilter, loading]);


    const handleDeleteClient = (clientId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le client sera désactivé et ne pourra plus être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, désactiver !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: clientId,
                    onSuccess: () => {
                        Swal.fire(
                            'Inactivé !',
                            'Le client a été désactivé avec succès.',
                            'success'
                        );
                        dispatch(clientActions.search({
                            query: ""
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de la désactivation.',
                            'error'
                        );
                    }
                }

                dispatch(clientActions.inactivate(payload));
            }
        });
    };

    const handleEdit = (client) => {
        const path = getClientEditPath(client);
        navigate(path, { state: { client } });
    };

    const handleBalanceSolde = (client) => {
        Swal.fire({
            title: 'Actualiser solde client',
            icon: 'info',
            showCancelButton: true,
            confirmButtonColor: 'rgba(10, 79, 149, 1)',
            cancelButtonColor: '#8c9094ff',
            confirmButtonText: 'Confirmer',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: client.id,
                    onSuccess: () => {
                        Swal.fire(
                            'Actualiser solde client',
                            'Mise à jour effectué avec succès.',
                            'success'
                        );
                        dispatch(clientActions.search({
                            query: ""
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de la mise à jour.',
                            'error'
                        );
                    }
                }

                dispatch(clientActions.balanceSolde(payload));
            }
        });
    }

    return (
        <div>
            {/* Header Section */}

            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUsers className="text-primary me-2" />
                                {t('client.etatClient')}
                            </h4>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? ( // Show spinner when loading
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom, email, téléphone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="d-flex gap-2 mt-2">
                                <Button
                                    color={balanceFilter === 'ALL' ? 'primary' : 'secondary'}
                                    size="sm"
                                    onClick={() => setBalanceFilter('ALL')}
                                    outline={balanceFilter !== 'ALL'}
                                >
                                    Tous
                                </Button>
                                <Button
                                    color={balanceFilter === 'DEBITEUR' ? 'primary' : 'secondary'}
                                    size="sm"
                                    onClick={() => setBalanceFilter('DEBITEUR')}
                                    outline={balanceFilter !== 'DEBITEUR'}
                                    title="Clients débiteurs (solde > 0)"
                                >
                                    Débiteur
                                </Button>
                                <Button
                                    color={balanceFilter === 'CREDITEUR' ? 'primary' : 'secondary'}
                                    size="sm"
                                    onClick={() => setBalanceFilter('CREDITEUR')}
                                    outline={balanceFilter !== 'CREDITEUR'}
                                    title="Clients créditeurs (solde < 0)"
                                >
                                    Créditeur
                                </Button>
                            </div>
                            <table className="table table-hover table-striped table-bordered mt-2" >
                                <thead className="thead-light">
                                    <tr>
                                        <th className="align-middle text-center">{t('client.code')}</th>
                                        <th className="align-middle text-center">{t('client.nom')}</th>
                                        <th className="align-middle text-center">{t('fournisseur.contact')}</th>
                                        <th className="align-middle text-center">{t('client.totalDebit')}</th>
                                        <th className="align-middle text-center">{t('client.totalCredit')}</th>
                                        <th className="align-middle text-center">{t('client.solde')}</th>
                                        <th className="align-middle text-center">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredClients.map(client => (
                                        <tr key={client.id}>
                                            <td>
                                                <strong>{client.code}</strong>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3">
                                                        {client.typeClient === 'PARTICULIER' ? (
                                                            <FaUser className="text-primary" />
                                                        ) : (
                                                            <FaRegBuilding className="text-info" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold">
                                                            {client.typeClient === 'PARTICULIER'
                                                                ? `${client.nom} ${client.prenom}`
                                                                : client.entreprise}
                                                        </div>
                                                        <small className="text-muted">
                                                            {client.typeClient === 'PARTICULIER' ? 'Particulier' : 'Entreprise'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">

                                                    <div>
                                                        <div className="fw-bold">
                                                            {client.telephone1}
                                                        </div>
                                                        <small className="text-muted">
                                                            {client.telephone2 || client.email || '-'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(client.totalDebit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(client.totalCredit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                {Number(client.solde) < 0 ? (
                                                    <span className="text-success fw-bold">+{formatPrice(Math.abs(client.solde))}</span>
                                                ) : Number(client.solde) > 0 ? (
                                                    <span className="text-danger fw-bold">-{formatPrice(Math.abs(client.solde))}</span>
                                                ) : (
                                                    <span className="text-dark fw-bold">{formatPrice(0)}</span>
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    color="success"
                                                    size="sm"
                                                    onClick={() => handleBalanceSolde(client)}
                                                >
                                                    <FaSync />
                                                </Button>

                                            </td>
                                        </tr>
                                    ))}

                                    {filteredClients.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">
                                                <div className="text-muted">{t('common.noClient')}</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-light fw-bold">
                                        <td colSpan="3" className="text-end">
                                            {t('common.totals')}
                                        </td>

                                        {/* Total Débit */}
                                        <td className="text-danger">
                                            {formatPrice(
                                                filteredClients.reduce((sum, c) => sum + Number(c.totalDebit || 0), 0)
                                            )}
                                        </td>

                                        {/* Total Crédit */}
                                        <td className="text-success">
                                            {formatPrice(
                                                filteredClients.reduce((sum, c) => sum + Number(c.totalCredit || 0), 0)
                                            )}
                                        </td>

                                        {/* Solde global */}
                                        <td>
                                            {(() => {
                                                const totalDebit = filteredClients.reduce(
                                                    (sum, c) => sum + Number(c.totalDebit || 0),
                                                    0
                                                );
                                                const totalCredit = filteredClients.reduce(
                                                    (sum, c) => sum + Number(c.totalCredit || 0),
                                                    0
                                                );
                                                const solde = totalDebit - totalCredit;

                                                if (solde < 0)
                                                    return (
                                                        <span className="text-success">
                                                            +{formatPrice(Math.abs(solde))}
                                                        </span>
                                                    );
                                                if (solde > 0)
                                                    return (
                                                        <span className="text-danger">
                                                            -{formatPrice(Math.abs(solde))}
                                                        </span>
                                                    );
                                                return <span className="text-dark">{formatPrice(0)}</span>;
                                            })()}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default EtatClient;